import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';
import {
  BuildingOfficeIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import Layout from '../components/layout/Layout';
import { dashboardAPI, companiesAPI, plansAPI, countriesAPI, paymentsAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const SuperAdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [expiringCompanies, setExpiringCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [countries, setCountries] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Payment management state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentsPagination, setPaymentsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [paymentMethodForm, setPaymentMethodForm] = useState({
    name: '',
    type: 'bank',
    description: '',
    priority: 0,
    bankDetails: {
      bankName: '',
      accountHolderName: '',
      accountNumber: '',
      ifscCode: '',
      branchName: '',
      swiftCode: ''
    },
    upiDetails: {
      upiId: '',
      qrCode: null
    },
    walletDetails: {
      walletName: '',
      walletAddress: ''
    },
    cryptoDetails: {
      currency: '',
      walletAddress: '',
      network: ''
    }
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('Fetching dashboard data...');
      
      const [dashboardResponse, companiesResponse, plansResponse, countriesResponse] = await Promise.all([
        dashboardAPI.getSuperAdminDashboard(),
        companiesAPI.getAll({ limit: 50 }), // Get more companies to filter expiring ones
        plansAPI.getAll(),
        countriesAPI.getAll()
      ]);

      console.log('Dashboard response:', dashboardResponse);
      console.log('Companies response:', companiesResponse);
      console.log('Plans response:', plansResponse);

      setDashboardData(dashboardResponse.data);
      const allCompanies = companiesResponse.data.companies || companiesResponse.data;
      setRecentCompanies(allCompanies.slice(0, 5)); // Keep recent companies for now
      
      // Filter companies with expiring or expired subscriptions
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));
      
      const expiring = allCompanies.filter(company => {
        if (!company.subscriptionEndDate) return false;
        const endDate = new Date(company.subscriptionEndDate);
        return endDate <= thirtyDaysFromNow && company.subscriptionStatus !== 'cancelled';
      });
      
      setExpiringCompanies(expiring);
      setPlans(plansResponse.data.plans || plansResponse.data);
      setCountries(countriesResponse.data || []);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Failed to load dashboard data');
      
      // Set mock data as fallback
      setDashboardData({
        overview: {
          totalCompanies: 15,
          totalUsers: 145,
          monthlyRevenue: 12500,
          activeSubscriptions: 12,
          companiesGrowth: 8.2,
          usersGrowth: 12.5,
          revenueGrowth: 15.3,
          subscriptionsGrowth: 6.1
        }
      });
      setRecentCompanies([
        { _id: '1', name: 'Demo Company 1', email: 'demo1@example.com', subscriptionStatus: 'active', createdAt: new Date() },
        { _id: '2', name: 'Demo Company 2', email: 'demo2@example.com', subscriptionStatus: 'active', createdAt: new Date() }
      ]);
      setExpiringCompanies([
        { 
          _id: '3', 
          name: 'Expiring Company', 
          email: 'expiring@example.com', 
          subscriptionStatus: 'active',
          subscriptionEndDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000), // 5 days from now
          plan: { name: 'Basic Plan' },
          usersCount: 5,
          leadsCount: 25
        }
      ]);
      setPlans([
        { _id: '1', name: 'Basic Plan', price: 29, maxUsers: 10, maxLeads: 100, storageLimitMB: 1024 },
        { _id: '2', name: 'Pro Plan', price: 59, maxUsers: 25, maxLeads: 500, storageLimitMB: 5120 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyCreated = (newCompany) => {
    // Refresh the companies list
    fetchDashboardData();
    toast.success(`Company "${newCompany.name}" created successfully!`);
  };

  const handleRenewSubscription = (company) => {
    setSelectedCompany(company);
    setShowRenewModal(true);
  };

  const handleRenewConfirm = async (newPlanId) => {
    try {
      await companiesAPI.update(selectedCompany._id, {
        plan: newPlanId,
        subscriptionStatus: 'active',
        subscriptionEndDate: new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)) // 30 days from now
      });
      
      toast.success(`Subscription renewed for ${selectedCompany.name}`);
      setShowRenewModal(false);
      setSelectedCompany(null);
      fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error renewing subscription:', error);
      toast.error('Failed to renew subscription');
    }
  };

  const handleViewCompany = (company) => {
    setSelectedCompany(company);
    setShowDetailModal(true);
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setShowEditModal(true);
    setShowDetailModal(false);
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      await companiesAPI.update(selectedCompany._id, {
        name: selectedCompany.name,
        email: selectedCompany.email,
        plan: selectedCompany.plan?._id || selectedCompany.plan,
        subscriptionStatus: selectedCompany.subscriptionStatus
      });
      
      toast.success(`Company ${selectedCompany.name} updated successfully`);
      setShowEditModal(false);
      setSelectedCompany(null);
      fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error updating company:', error);
      toast.error('Failed to update company');
    }
  };

  const getDaysUntilExpiry = (endDate) => {
    if (!endDate) return null;
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryStatus = (endDate) => {
    const days = getDaysUntilExpiry(endDate);
    if (days === null) return { status: 'unknown', color: 'bg-gray-100 text-gray-800' };
    if (days < 0) return { status: 'expired', color: 'bg-red-100 text-red-800' };
    if (days <= 7) return { status: 'critical', color: 'bg-red-100 text-red-800' };
    if (days <= 30) return { status: 'warning', color: 'bg-yellow-100 text-yellow-800' };
    return { status: 'active', color: 'bg-green-100 text-green-800' };
  };

  const getPlanAnalytics = () => {
    const analytics = plans.map(plan => {
      const subscribedCompanies = recentCompanies.filter(company => 
        company.plan && company.plan._id === plan._id && company.subscriptionStatus === 'active'
      );
      
      const filteredCompanies = selectedCountry === 'all' 
        ? subscribedCompanies 
        : subscribedCompanies.filter(company => company.country === selectedCountry);
      
      return {
        ...plan,
        companyCount: filteredCompanies.length,
        companies: filteredCompanies
      };
    }).filter(plan => plan.companyCount > 0); // Only show plans with subscriptions
    
    return analytics;
  };

  // Payment management functions
  const fetchPaymentMethods = async () => {
    try {
      const response = await paymentsAPI.getPaymentMethods();
      setPaymentMethods(response.data.data || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      toast.error('Failed to load payment methods');
    }
  };

  const fetchPayments = async (page = 1, limit = 10) => {
    try {
      const response = await paymentsAPI.getAllPayments({
        page,
        limit
      });
      setPayments(response.data.data || []);
      setPaymentsPagination({
        page: response.data.pagination?.page || 1,
        limit: response.data.pagination?.limit || 10,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.pages || 0
      });
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    }
  };

  const handleCreatePaymentMethod = () => {
    setSelectedPaymentMethod(null);
    setPaymentMethodForm({
      name: '',
      type: 'bank',
      description: '',
      priority: 0,
      bankDetails: {
        bankName: '',
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        branchName: '',
        swiftCode: ''
      },
      upiDetails: {
        upiId: '',
        qrCode: null
      },
      walletDetails: {
        walletName: '',
        walletAddress: ''
      },
      cryptoDetails: {
        currency: '',
        walletAddress: '',
        network: ''
      }
    });
    setShowPaymentMethodModal(true);
  };

  const handleEditPaymentMethod = (method) => {
    setSelectedPaymentMethod(method);
    setPaymentMethodForm({
      name: method.name || '',
      type: method.type || 'bank',
      description: method.description || '',
      priority: method.priority || 0,
      bankDetails: method.bankDetails || {
        bankName: '',
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        branchName: '',
        swiftCode: ''
      },
      upiDetails: method.upiDetails || {
        upiId: '',
        qrCode: null
      },
      walletDetails: method.walletDetails || {
        walletName: '',
        walletAddress: ''
      },
      cryptoDetails: method.cryptoDetails || {
        currency: '',
        walletAddress: '',
        network: ''
      }
    });
    setShowPaymentMethodModal(true);
  };

  const handleSavePaymentMethod = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('name', paymentMethodForm.name);
      formData.append('type', paymentMethodForm.type);
      formData.append('description', paymentMethodForm.description);
      formData.append('priority', paymentMethodForm.priority);

      if (paymentMethodForm.type === 'bank') {
        formData.append('bankDetails', JSON.stringify(paymentMethodForm.bankDetails));
      } else if (paymentMethodForm.type === 'upi') {
        formData.append('upiDetails', JSON.stringify({
          upiId: paymentMethodForm.upiDetails.upiId
        }));
        if (paymentMethodForm.upiDetails.qrCode) {
          formData.append('qrCode', paymentMethodForm.upiDetails.qrCode);
        }
      } else if (paymentMethodForm.type === 'wallet') {
        formData.append('walletDetails', JSON.stringify(paymentMethodForm.walletDetails));
      } else if (paymentMethodForm.type === 'crypto') {
        formData.append('cryptoDetails', JSON.stringify(paymentMethodForm.cryptoDetails));
      }

      if (selectedPaymentMethod) {
        await paymentsAPI.updatePaymentMethod(selectedPaymentMethod._id, formData);
        toast.success('Payment method updated successfully');
      } else {
        await paymentsAPI.createPaymentMethod(formData);
        toast.success('Payment method created successfully');
      }

      setShowPaymentMethodModal(false);
      fetchPaymentMethods();
    } catch (error) {
      console.error('Error saving payment method:', error);
      toast.error('Failed to save payment method');
    }
  };

  const handleDeletePaymentMethod = async (methodId) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) return;

    try {
      await paymentsAPI.deletePaymentMethod(methodId);
      toast.success('Payment method deleted successfully');
      fetchPaymentMethods();
    } catch (error) {
      console.error('Error deleting payment method:', error);
      toast.error('Failed to delete payment method');
    }
  };

  const handlePaymentStatusUpdate = async (paymentId, status, rejectionReason = '') => {
    try {
      await paymentsAPI.updatePaymentStatus(paymentId, {
        status,
        rejectionReason
      });

      toast.success(`Payment ${status} successfully`);
      fetchPayments(paymentsPagination.page);
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update payment status');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="large" text="Loading dashboard..." />
        </div>
      </Layout>
    );
  }

  const stats = [
    {
      name: 'Total Companies',
      value: dashboardData?.overview?.totalCompanies || 0,
      icon: BuildingOfficeIcon,
      color: 'from-cyan-500 to-blue-600',
      change: dashboardData?.overview?.companiesGrowth || 0,
    },
    {
      name: 'Total Users',
      value: dashboardData?.overview?.totalUsers || 0,
      icon: UsersIcon,
      color: 'from-emerald-500 to-teal-600',
      change: dashboardData?.overview?.usersGrowth || 0,
    },
    {
      name: 'Monthly Revenue',
      value: `$${(dashboardData?.overview?.monthlyRevenue || 0).toLocaleString()}`,
      icon: CurrencyDollarIcon,
      color: 'from-violet-500 to-purple-600',
      change: dashboardData?.overview?.revenueGrowth || 0,
    },
    {
      name: 'Active Subscriptions',
      value: dashboardData?.overview?.activeSubscriptions || 0,
      icon: ChartBarIcon,
      color: 'from-rose-500 to-pink-600',
      change: dashboardData?.overview?.subscriptionsGrowth || 0,
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="p-8 max-w-7xl mx-auto">
          {/* Modern Header */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-12 mb-12 shadow-2xl"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-y-48 translate-x-48"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-2xl translate-y-32 -translate-x-32"></div>

            {/* Floating Elements */}
            <div className="absolute top-8 left-8 w-20 h-20 bg-white/10 rounded-2xl rotate-12 animate-pulse"></div>
            <div className="absolute top-16 right-16 w-12 h-12 bg-white/10 rounded-full animate-bounce" style={{ animationDelay: '1s' }}></div>
            <div className="absolute bottom-12 left-12 w-16 h-16 bg-white/10 rounded-lg rotate-45 animate-pulse" style={{ animationDelay: '2s' }}></div>

            <div className="relative z-10">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="flex items-center space-x-6 mb-6"
              >
                <div className="p-4 bg-white/20 backdrop-blur-xl rounded-2xl shadow-2xl">
                  <ChartBarIcon className="h-12 w-12 text-white" />
                </div>
                <div>
                  <h1 className="text-5xl font-bold text-white mb-3 tracking-tight">
                    Super Admin Dashboard
                  </h1>
                  <p className="text-xl text-indigo-100 font-light leading-relaxed">
                    Comprehensive overview of your platform ecosystem
                  </p>
                </div>
              </motion.div>

              {/* Quick Stats Preview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8"
              >
                {stats.slice(0, 4).map((stat, index) => (
                  <motion.div
                    key={stat.name}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6 + index * 0.1, duration: 0.4 }}
                    className="bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/20"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 bg-gradient-to-br ${stat.color} rounded-xl shadow-lg`}>
                        <stat.icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white/80">{stat.name}</p>
                        <p className="text-2xl font-bold text-white">{stat.value}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>

          {/* Modern Stats Grid */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.name}
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  delay: 0.3 + index * 0.1,
                  duration: 0.6,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{
                  y: -8,
                  scale: 1.02,
                  transition: { duration: 0.2 }
                }}
                className="group relative overflow-hidden bg-white rounded-3xl shadow-xl hover:shadow-2xl border border-gray-100/50 backdrop-blur-xl"
              >
                {/* Gradient Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50/50 to-gray-100/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                {/* Animated Border */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 p-[1px]">
                  <div className="w-full h-full bg-white rounded-3xl"></div>
                </div>

                <div className="relative p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="relative group/icon">
                      {/* Glow Effect */}
                      <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${stat.color} opacity-50 blur-lg group-hover:opacity-75 transition-opacity duration-300`}></div>

                      {/* Main Icon Container */}
                      <div className={`relative p-4 rounded-2xl bg-gradient-to-br ${stat.color} shadow-xl group-hover:scale-110 group-hover:shadow-2xl transition-all duration-300 overflow-hidden`}>
                        {/* Inner Glow */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-black/10 rounded-2xl"></div>

                        {/* Animated Background Pattern */}
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300">
                          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/30 to-transparent rounded-2xl animate-pulse"></div>
                          <div className="absolute bottom-0 right-0 w-3/4 h-3/4 bg-gradient-to-tl from-black/10 to-transparent rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                        </div>

                        {/* Icon */}
                        <stat.icon className="relative h-8 w-8 text-white drop-shadow-lg group-hover:scale-105 transition-transform duration-300" />

                        {/* Shine Effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                      </div>
                    </div>
                    <div className="text-right">
                      {stat.change !== undefined && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.5 + index * 0.1 }}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            stat.change >= 0
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                              : 'bg-red-100 text-red-700 border border-red-200'
                          }`}
                        >
                          {stat.change >= 0 ? (
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-3.707-8.293l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 00-1.414 1.414z" clipRule="evenodd" />
                            </svg>
                          )}
                          {Math.abs(stat.change).toFixed(1)}%
                        </motion.div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      {stat.name}
                    </h3>
                    <p className="text-4xl font-bold text-gray-900 group-hover:text-gray-700 transition-colors duration-300">
                      {stat.value}
                    </p>
                  </div>

                  {/* Decorative Element */}
                  <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                </div>
              </motion.div>
            ))}
          </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Subscription Management */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="relative overflow-hidden bg-white rounded-3xl shadow-xl border border-gray-100/50 backdrop-blur-xl"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-red-50/20 to-pink-50/30"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-400/10 to-red-400/10 rounded-full blur-2xl"></div>

            <div className="relative p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <div className="p-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl mr-3 shadow-lg">
                      <ExclamationTriangleIcon className="h-6 w-6 text-white" />
                    </div>
                    Subscription Management
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">Companies with expiring or expired subscriptions</p>
                </div>
              </div>

              <div className="space-y-4 max-h-96 overflow-y-auto">
                {expiringCompanies.length > 0 ? (
                  expiringCompanies.map((company, index) => {
                    const expiryInfo = getExpiryStatus(company.subscriptionEndDate);
                    const daysUntilExpiry = getDaysUntilExpiry(company.subscriptionEndDate);

                    return (
                      <motion.div
                        key={company._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 * index }}
                        whileHover={{ scale: 1.02, y: -2 }}
                        className="group relative overflow-hidden bg-gradient-to-r from-white to-gray-50/50 rounded-2xl border border-gray-200/50 hover:border-orange-300/50 shadow-sm hover:shadow-lg transition-all duration-300"
                      >
                        {/* Hover Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                        <div className="relative p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-3">
                                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                                  <span className="text-white font-bold text-sm">
                                    {company.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <h4 className="text-lg font-semibold text-gray-900 group-hover:text-gray-700 transition-colors">
                                    {company.name}
                                  </h4>
                                  <p className="text-sm text-gray-600">{company.email}</p>
                                </div>
                                <motion.span
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${expiryInfo.color} shadow-sm`}
                                >
                                  <div className={`w-2 h-2 rounded-full mr-2 ${
                                    expiryInfo.status === 'expired' ? 'bg-red-500' :
                                    expiryInfo.status === 'critical' ? 'bg-orange-500' :
                                    expiryInfo.status === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
                                  }`}></div>
                                  {expiryInfo.status === 'expired' ? 'Expired' :
                                   expiryInfo.status === 'critical' ? 'Expires Soon' :
                                   expiryInfo.status === 'warning' ? 'Expiring Soon' : 'Active'}
                                </motion.span>
                              </div>

                              <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                                <div className="bg-white/60 rounded-lg p-3 text-center">
                                  <p className="font-semibold text-gray-900">{company.plan?.name || 'No plan'}</p>
                                  <p className="text-gray-600">Plan</p>
                                </div>
                                <div className="bg-white/60 rounded-lg p-3 text-center">
                                  <p className="font-semibold text-gray-900">{company.usersCount || 0}</p>
                                  <p className="text-gray-600">Users</p>
                                </div>
                                <div className="bg-white/60 rounded-lg p-3 text-center">
                                  <p className="font-semibold text-gray-900">{company.leadsCount || 0}</p>
                                  <p className="text-gray-600">Leads</p>
                                </div>
                              </div>

                              {daysUntilExpiry !== null && (
                                <div className="flex items-center bg-white/60 rounded-lg p-3">
                                  <ClockIcon className="h-4 w-4 text-gray-500 mr-2" />
                                  <span className="text-sm text-gray-700 font-medium">
                                    {daysUntilExpiry < 0
                                      ? `Expired ${Math.abs(daysUntilExpiry)} days ago`
                                      : daysUntilExpiry === 0
                                        ? 'Expires today'
                                        : `Expires in ${daysUntilExpiry} days`
                                    }
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col space-y-2 ml-6">
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleRenewSubscription(company)}
                                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-semibold rounded-xl hover:from-green-600 hover:to-emerald-600 shadow-lg hover:shadow-xl transition-all duration-200"
                              >
                                <ArrowPathIcon className="h-4 w-4 mr-2" />
                                Renew
                              </motion.button>

                              <div className="flex space-x-1">
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleViewCompany(company)}
                                  className="p-2 text-gray-400 hover:text-blue-600 bg-white/60 hover:bg-blue-50 rounded-lg transition-all duration-200"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleEditCompany(company)}
                                  className="p-2 text-gray-400 hover:text-purple-600 bg-white/60 hover:bg-purple-50 rounded-lg transition-all duration-200"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </motion.button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                      <ClockIcon className="h-10 w-10 text-white" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">All Good!</h4>
                    <p className="text-gray-600">All subscriptions are up to date</p>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Plan Analytics */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="relative overflow-hidden bg-white rounded-3xl shadow-xl border border-gray-100/50 backdrop-blur-xl"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-indigo-50/20 to-purple-50/30"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-2xl"></div>

            <div className="relative p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl mr-3 shadow-lg">
                      <ChartBarIcon className="h-6 w-6 text-white" />
                    </div>
                    Plan Analytics
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">Company subscription distribution by plan</p>
                </div>
                
                {/* Country Filter */}
                <div className="flex items-center space-x-3">
                  <label className="text-sm font-medium text-gray-700">Filter by Country:</label>
                  <select
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 relative z-10"
                  >
                    <option value="all">All Countries</option>
                    {countries.map(country => (
                      <option key={country._id} value={country._id}>{country.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                {getPlanAnalytics().length > 0 ? (
                  getPlanAnalytics().map((plan, index) => (
                    <motion.div
                      key={plan._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index }}
                      whileHover={{ scale: 1.02, y: -2 }}
                      className="group relative overflow-hidden bg-gradient-to-r from-white to-gray-50/50 rounded-2xl border border-gray-200/50 hover:border-blue-300/50 shadow-sm hover:shadow-lg transition-all duration-300"
                    >
                      {/* Hover Gradient */}
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                      <div className="relative p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-3">
                              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                                <span className="text-white font-bold text-lg">
                                  {plan.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <h4 className="text-xl font-bold text-gray-900 group-hover:text-gray-700 transition-colors">
                                  {plan.name}
                                </h4>
                                <div className="flex items-center space-x-4">
                                  <span className="text-2xl font-bold text-green-600">
                                    {plan?.currency.symbol}{plan.price}
                                  </span>
                                  <span className="text-sm text-gray-600">/{plan.billingCycle}</span>
                                  <div className="flex items-center space-x-2">
                                    <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                                    <span className="text-lg font-bold text-blue-600">{plan.companyCount}</span>
                                    <span className="text-sm text-gray-600">companies</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4 mb-4">
                              <div className="bg-white/60 rounded-lg p-3 text-center">
                                <p className="text-lg font-bold text-gray-900">{(plan.storageLimitMB / 1024).toFixed(1)}GB</p>
                                <p className="text-xs text-gray-600">Storage</p>
                              </div>
                              <div className="bg-white/60 rounded-lg p-3 text-center">
                                <p className="text-lg font-bold text-gray-900">{plan.maxUsers}</p>
                                <p className="text-xs text-gray-600">Users</p>
                              </div>
                              
                            </div>
                          </div>

                          <div className="flex flex-col items-end space-y-3 ml-6">
                            <motion.span
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold shadow-sm bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                            >
                              <div className="w-2 h-2 rounded-full mr-2 bg-white"></div>
                              Active Plan
                            </motion.span>

                            <div className="text-right">
                              <p className="text-sm text-gray-600">{plan.billingCycle} Revenue</p>
                              <p className="text-lg font-bold text-green-600">
                                {plan?.currency.symbol}{(plan.price * plan.companyCount).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-20 h-20 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                      <ChartBarIcon className="h-10 w-10 text-white" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">No Active Subscriptions</h4>
                    <p className="text-gray-600">
                      {selectedCountry === 'all' 
                        ? 'No companies are currently subscribed to any plans' 
                        : `No companies in the selected country are subscribed to any plans`
                      }
                    </p>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* System Health & Metrics */}
        {dashboardData?.systemMetrics && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8 relative overflow-hidden bg-white rounded-3xl shadow-xl border border-gray-100/50 backdrop-blur-xl"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/30 via-teal-50/20 to-cyan-50/30"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-400/10 to-cyan-400/10 rounded-full blur-2xl"></div>

            <div className="relative p-8">
              <div className="flex items-center mb-8">
                <div className="p-2 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-xl mr-3 shadow-lg">
                  <ChartBarIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">System Health & Metrics</h3>
                  <p className="text-sm text-gray-600 mt-1">Platform performance and resource utilization</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="group relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300"></div>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-white/20 rounded-xl">
                        <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold">
                          {(dashboardData.systemMetrics.totalStorageUsed / 1024).toFixed(1)}
                        </p>
                        <p className="text-sm opacity-90">GB Used</p>
                      </div>
                    </div>
                    <h4 className="text-lg font-semibold mb-2">Storage Usage</h4>
                    <p className="text-sm opacity-80">Total storage consumed by all companies</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="group relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300"></div>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-white/20 rounded-xl">
                        <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold">
                          {dashboardData.systemMetrics.totalFiles || 0}
                        </p>
                        <p className="text-sm opacity-90">Files</p>
                      </div>
                    </div>
                    <h4 className="text-lg font-semibold mb-2">Files Uploaded</h4>
                    <p className="text-sm opacity-80">Total documents and files stored</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="group relative overflow-hidden bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300"></div>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-white/20 rounded-xl">
                        <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold">
                          {dashboardData.systemMetrics.totalLeads || 0}
                        </p>
                        <p className="text-sm opacity-90">Leads</p>
                      </div>
                    </div>
                    <h4 className="text-lg font-semibold mb-2">Total Leads</h4>
                    <p className="text-sm opacity-80">All leads across all companies</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>

      {/* Renew Subscription Modal */}
      {showRenewModal && selectedCompany && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Renew Subscription - {selectedCompany.name}
                </h3>
                <button
                  onClick={() => setShowRenewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">Select a new plan for this company:</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {plans.map((plan) => (
                    <div
                      key={plan._id}
                      onClick={() => handleRenewConfirm(plan._id)}
                      className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{plan.name}</h4>
                          <p className="text-sm text-gray-600">${plan.price}/month</p>
                          <p className="text-xs text-gray-500">
                            {plan.maxUsers} users • {plan.maxLeads} leads • {(plan.storageLimitMB / 1024).toFixed(1)}GB storage
                          </p>
                        </div>
                        <ArrowPathIcon className="h-5 w-5 text-blue-500" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowRenewModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Management Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="mt-12 bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 rounded-3xl shadow-xl border border-gray-200/50 overflow-hidden"
      >
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl shadow-lg">
                <CurrencyDollarIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Payment Management</h2>
                <p className="text-blue-100 mt-1">Manage payment methods and process payments</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  fetchPaymentMethods();
                  setShowPaymentsModal(true);
                }}
                className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded-xl hover:bg-white/30 transition-all duration-200 shadow-lg"
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                View Payments
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCreatePaymentMethod}
                className="inline-flex items-center px-4 py-2 bg-white text-gray-900 border border-white rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-lg font-semibold"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Payment Method
              </motion.button>
            </div>
          </div>
        </div>

        <div className="p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Payment Methods */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                <h3 className="text-lg font-bold text-gray-900 flex items-center">
                  <BuildingOfficeIcon className="h-5 w-5 mr-2 text-blue-500" />
                  Payment Methods ({paymentMethods.length})
                </h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {paymentMethods.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {paymentMethods.map((method) => (
                      <motion.div
                        key={method._id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="p-4 hover:bg-gray-50 transition-colors duration-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${
                              method.type === 'bank' ? 'bg-blue-500' :
                              method.type === 'upi' ? 'bg-green-500' :
                              method.type === 'wallet' ? 'bg-purple-500' : 'bg-orange-500'
                            }`}>
                              <span className="text-white font-bold text-sm">
                                {method.type === 'bank' ? 'B' :
                                 method.type === 'upi' ? 'U' :
                                 method.type === 'wallet' ? 'W' : 'C'}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900">{method.name}</h4>
                              <p className="text-sm text-gray-600 capitalize">{method.type}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleEditPaymentMethod(method)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleDeletePaymentMethod(method._id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <CurrencyDollarIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Payment Methods</h3>
                    <p className="text-gray-600 mb-4">Add payment methods to accept payments from companies</p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleCreatePaymentMethod}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add First Method
                    </motion.button>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Payments */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                <h3 className="text-lg font-bold text-gray-900 flex items-center">
                  <ClockIcon className="h-5 w-5 mr-2 text-green-500" />
                  Recent Payments ({payments.filter(p => p.status === 'pending').length} pending)
                </h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {payments.filter(p => p.status === 'pending').length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {payments.filter(p => p.status === 'pending').slice(0, 5).map((payment) => (
                      <motion.div
                        key={payment._id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="p-4 hover:bg-gray-50 transition-colors duration-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{payment.company?.name}</h4>
                            <p className="text-sm text-gray-600">{payment.plan?.name} - {payment.currency?.symbol}{payment.amount}</p>
                            <p className="text-xs text-gray-500">{new Date(payment.createdAt).toLocaleDateString()}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handlePaymentStatusUpdate(payment._id, 'approved')}
                              className="px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors"
                            >
                              Approve
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => {
                                const reason = prompt('Rejection reason:');
                                if (reason) handlePaymentStatusUpdate(payment._id, 'rejected', reason);
                              }}
                              className="px-3 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
                            >
                              Reject
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <CheckCircleIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pending Payments</h3>
                    <p className="text-gray-600">All payments have been processed</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Company Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedCompany && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-green-500 to-teal-500 px-6 py-4 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                      <EyeIcon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Company Details</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleEditCompany(selectedCompany)}
                      className="p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors duration-200"
                    >
                      <PencilIcon className="h-5 w-5 text-white" />
                    </motion.button>
                    <button
                      type="button"
                      onClick={() => setShowDetailModal(false)}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                    >
                      <XMarkIcon className="h-6 w-6 text-white" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-8">
                {/* Company Header Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6 border border-blue-100"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                        {selectedCompany.name?.charAt(0)?.toUpperCase() || 'C'}
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">{selectedCompany.name}</h2>
                        <p className="text-gray-600 flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {selectedCompany.email}
                        </p>
                        {selectedCompany.phone && (
                          <p className="text-gray-600 flex items-center mt-1">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {selectedCompany.phone}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${
                        selectedCompany.subscriptionStatus === 'active' ? 'bg-green-100 text-green-800 border border-green-200' :
                        selectedCompany.subscriptionStatus === 'trial' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                        selectedCompany.subscriptionStatus === 'suspended' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                        'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        <div className={`w-2 h-2 rounded-full mr-2 ${
                          selectedCompany.subscriptionStatus === 'active' ? 'bg-green-500' :
                          selectedCompany.subscriptionStatus === 'trial' ? 'bg-blue-500' :
                          selectedCompany.subscriptionStatus === 'suspended' ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}></div>
                        {selectedCompany.subscriptionStatus?.charAt(0).toUpperCase() + selectedCompany.subscriptionStatus?.slice(1)}
                      </span>
                    </div>
                  </div>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Company Information */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <BuildingOfficeIcon className="h-5 w-5 text-blue-500 mr-2" />
                      Company Information
                    </h3>

                    <div className="space-y-4">
                      {selectedCompany.website && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Website</span>
                          <a
                            href={selectedCompany.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
                          >
                            Visit Website
                            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        </div>
                      )}

                      {selectedCompany.country && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Country</span>
                          <span className="text-sm text-gray-900 flex items-center">
                            {selectedCompany.country.flag && <span className="mr-2">{selectedCompany.country.flag}</span>}
                            {selectedCompany.country.name || selectedCompany.country}
                          </span>
                        </div>
                      )}

                      {selectedCompany.currency && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Currency</span>
                          <span className="text-sm text-gray-900 flex items-center">
                            {selectedCompany.currency.symbol && <span className="mr-1 font-semibold">{selectedCompany.currency.symbol}</span>}
                            {selectedCompany.currency.name || selectedCompany.currency}
                            {selectedCompany.currency.code && <span className="text-gray-500 ml-1">({selectedCompany.currency.code})</span>}
                          </span>
                        </div>
                      )}

                      {selectedCompany.address && (
                        <div className="p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600 block mb-2">Address</span>
                          <p className="text-sm text-gray-900 leading-relaxed">
                            {selectedCompany.address.street && `${selectedCompany.address.street}, `}
                            {selectedCompany.address.city && `${selectedCompany.address.city}, `}
                            {selectedCompany.address.state && `${selectedCompany.address.state} `}
                            {selectedCompany.address.zipCode && `${selectedCompany.address.zipCode}`}
                            {selectedCompany.address.country && `, ${selectedCompany.address.country}`}
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>

                  {/* Plan & Usage Information */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="space-y-6"
                  >
                    {/* Plan Information */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Plan Details
                      </h3>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600">Current Plan</span>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                            selectedCompany.plan?.name?.toLowerCase().includes('enterprise') ? 'bg-purple-100 text-purple-800' :
                            selectedCompany.plan?.name?.toLowerCase().includes('pro') ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {selectedCompany.plan?.name || selectedCompany.plan || 'No Plan'}
                          </span>
                        </div>

                        {selectedCompany.plan && selectedCompany.plan.price && (
                          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                            <span className="text-sm font-medium text-gray-600">Monthly Cost</span>
                            <span className="text-lg font-bold text-green-700">
                              {selectedCompany.plan.currency?.symbol || '$'}{selectedCompany.plan.price}
                              <span className="text-sm font-normal text-green-600">/{selectedCompany.plan.billingCycle || 'month'}</span>
                            </span>
                          </div>
                        )}

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Users</span>
                          <div className="flex items-center space-x-2">
                            <UsersIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-semibold text-gray-900">
                              {selectedCompany.usersCount || 0} / {selectedCompany.plan?.maxUsers || 0}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Leads</span>
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <span className="text-sm font-semibold text-gray-900">
                              {selectedCompany.leadsCount || 0} / {selectedCompany.plan?.maxLeads || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Company Modal */}
      <AnimatePresence>
        {showEditModal && selectedCompany && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
            >
              <form onSubmit={handleUpdateCompany}>
                <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-4 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                        <PencilIcon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white">Edit Company</h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                    >
                      <XMarkIcon className="h-6 w-6 text-white" />
                    </button>
                  </div>
                </div>

                <div className="px-6 py-4 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={selectedCompany.name || ''}
                        onChange={(e) => setSelectedCompany({ ...selectedCompany, name: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={selectedCompany.email || ''}
                        onChange={(e) => setSelectedCompany({ ...selectedCompany, email: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={selectedCompany.subscriptionStatus || 'active'}
                        onChange={(e) => setSelectedCompany({ ...selectedCompany, subscriptionStatus: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="active">Active</option>
                        <option value="trial">Trial</option>
                        <option value="suspended">Suspended</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plan
                    </label>
                    <select
                      value={selectedCompany.plan?._id || selectedCompany.plan || ''}
                      onChange={(e) => {
                        const selectedPlan = plans.find(p => p._id === e.target.value);
                        setSelectedCompany({ ...selectedCompany, plan: selectedPlan });
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Plan</option>
                      {plans.map(plan => (
                        <option key={plan._id} value={plan._id}>
                          {plan.name} - ${plan.price}/month
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="px-6 py-6 bg-gray-50 rounded-b-2xl flex justify-end space-x-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 border border-transparent rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                  >
                    Update Company
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Method Modal */}
      <AnimatePresence>
        {showPaymentMethodModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                      <CurrencyDollarIcon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      {selectedPaymentMethod ? 'Edit Payment Method' : 'Add Payment Method'}
                    </h3>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowPaymentMethodModal(false)}
                    className="p-2 bg-white/20 backdrop-blur-sm rounded-lg text-white hover:bg-white/30 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </motion.button>
                </div>
              </div>

              <form onSubmit={handleSavePaymentMethod} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Method Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={paymentMethodForm.name}
                      onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., Bank Transfer, UPI, PayPal"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type *
                    </label>
                    <select
                      required
                      value={paymentMethodForm.type}
                      onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, type: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="bank">Bank Transfer</option>
                      <option value="upi">UPI</option>
                      <option value="wallet">Digital Wallet</option>
                      <option value="crypto">Cryptocurrency</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={paymentMethodForm.description}
                    onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, description: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Optional description..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority (Higher = Display First)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={paymentMethodForm.priority}
                    onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, priority: parseInt(e.target.value) || 0 })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Bank Details */}
                {paymentMethodForm.type === 'bank' && (
                  <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-900">Bank Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Bank Name"
                        value={paymentMethodForm.bankDetails.bankName}
                        onChange={(e) => setPaymentMethodForm({
                          ...paymentMethodForm,
                          bankDetails: { ...paymentMethodForm.bankDetails, bankName: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Account Holder Name"
                        value={paymentMethodForm.bankDetails.accountHolderName}
                        onChange={(e) => setPaymentMethodForm({
                          ...paymentMethodForm,
                          bankDetails: { ...paymentMethodForm.bankDetails, accountHolderName: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Account Number"
                        value={paymentMethodForm.bankDetails.accountNumber}
                        onChange={(e) => setPaymentMethodForm({
                          ...paymentMethodForm,
                          bankDetails: { ...paymentMethodForm.bankDetails, accountNumber: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="IFSC Code"
                        value={paymentMethodForm.bankDetails.ifscCode}
                        onChange={(e) => setPaymentMethodForm({
                          ...paymentMethodForm,
                          bankDetails: { ...paymentMethodForm.bankDetails, ifscCode: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Branch Name"
                        value={paymentMethodForm.bankDetails.branchName}
                        onChange={(e) => setPaymentMethodForm({
                          ...paymentMethodForm,
                          bankDetails: { ...paymentMethodForm.bankDetails, branchName: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="SWIFT Code (Optional)"
                        value={paymentMethodForm.bankDetails.swiftCode}
                        onChange={(e) => setPaymentMethodForm({
                          ...paymentMethodForm,
                          bankDetails: { ...paymentMethodForm.bankDetails, swiftCode: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}

                {/* UPI Details */}
                {paymentMethodForm.type === 'upi' && (
                  <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-900">UPI Details</h4>
                    <div className="space-y-4">
                      <input
                        type="text"
                        placeholder="UPI ID (e.g., merchant@paytm)"
                        value={paymentMethodForm.upiDetails.upiId}
                        onChange={(e) => setPaymentMethodForm({
                          ...paymentMethodForm,
                          upiDetails: { ...paymentMethodForm.upiDetails, upiId: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          QR Code (Optional)
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setPaymentMethodForm({
                            ...paymentMethodForm,
                            upiDetails: { ...paymentMethodForm.upiDetails, qrCode: e.target.files[0] }
                          })}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {paymentMethodForm.upiDetails.qrCode && (
                          <p className="text-sm text-gray-600 mt-1">
                            Selected: {paymentMethodForm.upiDetails.qrCode.name || 'QR Code image'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Wallet Details */}
                {paymentMethodForm.type === 'wallet' && (
                  <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-900">Digital Wallet Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Wallet Name (e.g., PayPal, Paytm)"
                        value={paymentMethodForm.walletDetails.walletName}
                        onChange={(e) => setPaymentMethodForm({
                          ...paymentMethodForm,
                          walletDetails: { ...paymentMethodForm.walletDetails, walletName: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Wallet Address/ID"
                        value={paymentMethodForm.walletDetails.walletAddress}
                        onChange={(e) => setPaymentMethodForm({
                          ...paymentMethodForm,
                          walletDetails: { ...paymentMethodForm.walletDetails, walletAddress: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}

                {/* Crypto Details */}
                {paymentMethodForm.type === 'crypto' && (
                  <div className="space-y-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <h4 className="font-semibold text-orange-900">Cryptocurrency Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <input
                        type="text"
                        placeholder="Currency (e.g., BTC, ETH)"
                        value={paymentMethodForm.cryptoDetails.currency}
                        onChange={(e) => setPaymentMethodForm({
                          ...paymentMethodForm,
                          cryptoDetails: { ...paymentMethodForm.cryptoDetails, currency: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Wallet Address"
                        value={paymentMethodForm.cryptoDetails.walletAddress}
                        onChange={(e) => setPaymentMethodForm({
                          ...paymentMethodForm,
                          cryptoDetails: { ...paymentMethodForm.cryptoDetails, walletAddress: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Network (e.g., ERC20, BSC)"
                        value={paymentMethodForm.cryptoDetails.network}
                        onChange={(e) => setPaymentMethodForm({
                          ...paymentMethodForm,
                          cryptoDetails: { ...paymentMethodForm.cryptoDetails, network: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => setShowPaymentMethodModal(false)}
                    className="px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-100 border border-gray-300 rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 border border-transparent rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                  >
                    {selectedPaymentMethod ? 'Update Method' : 'Create Method'}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payments Modal */}
      <AnimatePresence>
        {showPaymentsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-green-600 to-teal-600 px-6 py-4 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                      <EyeIcon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Payment Management</h3>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowPaymentsModal(false)}
                    className="p-2 bg-white/20 backdrop-blur-sm rounded-lg text-white hover:bg-white/30 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </motion.button>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-6 flex justify-between items-center">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">All Payments</h4>
                    <p className="text-gray-600">Manage and approve payment requests</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={fetchPayments}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-2" />
                    Refresh
                  </motion.button>
                </div>

                {/* Modern Table Design */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Company & Plan
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Amount & Method
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Transaction Details
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Status & Date
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {payments.map((payment, index) => (
                          <motion.tr
                            key={payment._id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="hover:bg-gray-50 transition-colors duration-150"
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                                  <BuildingOfficeIcon className="h-5 w-5 text-white" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-semibold text-gray-900">
                                    {payment.company?.name}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {payment.plan?.name}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-semibold text-gray-900">
                                {payment.currency?.symbol}{payment.amount}
                              </div>
                              <div className="text-sm text-gray-600 capitalize flex items-center mt-1">
                                <CreditCardIcon className="h-4 w-4 mr-1" />
                                {payment.paymentMethod?.type}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="space-y-2">
                                {payment.transactionId && (
                                  <div className="flex items-center">
                                    <span className="text-xs text-gray-500 mr-2">Txn ID:</span>
                                    <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-800">
                                      {payment.transactionId}
                                    </span>
                                  </div>
                                )}
                                {payment.referenceNumber && (
                                  <div className="flex items-center">
                                    <span className="text-xs text-gray-500 mr-2">Ref:</span>
                                    <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded text-gray-800">
                                      {payment.referenceNumber}
                                    </span>
                                  </div>
                                )}
                                {payment.paymentProof && (
                                  <div className="flex items-center">
                                    <span className="text-xs text-gray-500 mr-2">Proof:</span>
                                    <a
                                      href={payment.paymentProof.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                                    >
                                      <DocumentIcon className="h-3 w-3 mr-1" />
                                      View
                                    </a>
                                  </div>
                                )}
                                {!payment.transactionId && !payment.referenceNumber && !payment.paymentProof && (
                                  <span className="text-xs text-gray-400">No details</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col space-y-2">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full w-fit ${
                                  payment.status === 'approved' ? 'bg-green-100 text-green-800' :
                                  payment.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {payment.status}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {new Date(payment.createdAt).toLocaleDateString()}
                                </span>
                                {payment.status === 'rejected' && payment.rejectionReason && (
                                  <div className="text-xs text-red-600 bg-red-50 p-2 rounded mt-1">
                                    {payment.rejectionReason}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {payment.status === 'pending' && (
                                <div className="flex space-x-2">
                                  <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handlePaymentStatusUpdate(payment._id, 'approved')}
                                    className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                                  >
                                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                                    Approve
                                  </motion.button>
                                  <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => {
                                      const reason = prompt('Rejection reason:');
                                      if (reason) handlePaymentStatusUpdate(payment._id, 'rejected', reason);
                                    }}
                                    className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors"
                                  >
                                    <XCircleIcon className="h-3 w-3 mr-1" />
                                    Reject
                                  </motion.button>
                                </div>
                              )}
                              {payment.status !== 'pending' && (
                                <span className="text-xs text-gray-400">
                                  {payment.status === 'approved' ? 'Approved' : 'Processed'}
                                </span>
                              )}
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {paymentsPagination.totalPages > 1 && (
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          Showing {((paymentsPagination.page - 1) * paymentsPagination.limit) + 1} to {Math.min(paymentsPagination.page * paymentsPagination.limit, paymentsPagination.total)} of {paymentsPagination.total} payments
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => fetchPayments(paymentsPagination.page - 1)}
                            disabled={paymentsPagination.page <= 1}
                            className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>

                          {/* Page Numbers */}
                          {Array.from({ length: Math.min(5, paymentsPagination.totalPages) }, (_, i) => {
                            const pageNum = Math.max(1, Math.min(paymentsPagination.totalPages - 4, paymentsPagination.page - 2)) + i;
                            if (pageNum > paymentsPagination.totalPages) return null;
                            return (
                              <button
                                key={pageNum}
                                onClick={() => fetchPayments(pageNum)}
                                className={`px-3 py-1 text-sm border rounded-md ${
                                  pageNum === paymentsPagination.page
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}

                          <button
                            onClick={() => fetchPayments(paymentsPagination.page + 1)}
                            disabled={paymentsPagination.page >= paymentsPagination.totalPages}
                            className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {payments.length === 0 && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <CurrencyDollarIcon className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Payments Found</h3>
                      <p className="text-gray-600">No payment records available</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    
    </Layout>
  );
};

export default SuperAdminDashboard;